import React, { useState, useEffect } from 'react';
import { analyticsService } from '../services/analyticsService';

const AnalyticsDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [recentVisits, setRecentVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const [analyticsData, visitsData] = await Promise.all([
        analyticsService.getAnalytics(),
        analyticsService.getPageVisits(20)
      ]);
      
      if (!analyticsData) {
        setError('Visit tracking is turned off: the site no longer uses a database. Use Netlify Analytics for traffic numbers.');
      }
      setAnalytics(analyticsData);
      setRecentVisits(visitsData);
    } catch (err) {
      setError('Failed to fetch analytics data');
      console.error('Error fetching analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const formatPagePath = (path) => {
    const pathMap = {
      '/': 'Home',
      '/about': 'About Us',
      '/contact': 'Contact',
      '/upload': 'Upload Article',
      '/tracking': 'Article Tracking',
      '/admin': 'Admin Panel',
      '/privacy-policy': 'Privacy Policy',
      '/copyright': 'Copyright',
      '/tech-team': 'Tech Team',
      '/terms-of-service': 'Terms of Service',
      '/disclaimer': 'Disclaimer',
      '/call-articles': 'Call for Articles',
      '/types-of-articles': 'Types of Articles',
      '/information-for-authors': 'Information for Authors',
      '/submission-guidelines': 'Submission Guidelines',
      '/article-processing-charge': 'Article Processing Charge (APC)'
    };
    return pathMap[path] || path;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-4">Analytics Dashboard</h2>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-4">Analytics Dashboard</h2>
        <div className="text-red-600">{error}</div>
        <button 
          onClick={fetchAnalytics}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Analytics Dashboard</h2>
        <button 
          onClick={fetchAnalytics}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-blue-800">Total Visitors</h3>
          <p className="text-3xl font-bold text-blue-600">
            {analytics?.visitorStats?.totalVisitors || 0}
          </p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-green-800">Total Page Views</h3>
          <p className="text-3xl font-bold text-green-600">
            {analytics?.pageStats?.totalPageViews || 0}
          </p>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-purple-800">Pages Tracked</h3>
          <p className="text-3xl font-bold text-purple-600">
            {analytics?.pageStats?.pages ? Object.keys(analytics.pageStats.pages).length : 0}
          </p>
        </div>
      </div>

      {/* Page Statistics */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold mb-4">Page Statistics</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Page</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visits</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Visit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {analytics?.pageStats?.pages ? 
                Object.entries(analytics.pageStats.pages)
                  .sort(([,a], [,b]) => b.visits - a.visits)
                  .map(([pageKey, stats]) => (
                    <tr key={pageKey} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm text-gray-900">
                        {formatPagePath('/' + pageKey.replace(/_/g, '/'))}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900 font-medium">
                        {stats.visits}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-500">
                        {formatDate(stats.lastVisit)}
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="3" className="px-4 py-2 text-sm text-gray-500 text-center">
                        No page statistics available
                      </td>
                    </tr>
                  )
              }
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Visits */}
      <div>
        <h3 className="text-xl font-semibold mb-4">Recent Visits</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Page</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visitor ID</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Device</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {recentVisits.length > 0 ? 
                recentVisits.map((visit) => (
                  <tr key={visit.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm text-gray-900">
                      {formatPagePath(visit.pagePath)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500 font-mono">
                      {visit.visitorId.substring(0, 12)}...
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500">
                      {formatDate(visit.timestamp)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500">
                      {visit.screenResolution}
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="4" className="px-4 py-2 text-sm text-gray-500 text-center">
                      No recent visits
                    </td>
                  </tr>
                )
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard; 